import { apiGet, apiPost, apiPatch, apiClient } from '@/services/api/apiClient';
import { API_ENDPOINTS } from '@/constants/endpoints';

export type EnquiryStatus =
  | 'new'
  | 'contacted'
  | 'in_progress'
  | 'qualified'
  | 'converted'
  | 'not_interested'
  | 'closed';

export interface EnquiryComment {
  _id: string;
  text: string;
  addedBy?: { _id: string; name?: string };
  createdAt: string;
}

export interface EnquiryServiceProductRef {
  _id: string;
  title?: string;
  serviceType?: 'product' | 'service';
  price?: number;
}

export interface EnquiryBusinessRef {
  _id: string;
  businessName?: string;
}

export interface EnquiryUserRef {
  _id?: string;
  name?: string;
  email?: string;
  whatsappNumber?: string;
  userId?: string;
  city?: string;
  state?: string;
}

export interface Enquiry {
  _id: string;
  serviceProductId: string | EnquiryServiceProductRef;
  businessId: string | EnquiryBusinessRef;
  ownerId: string;
  userId: string | EnquiryUserRef;
  name: string;
  mobileNumber: string;
  email: string;
  whatsappNumber?: string;
  location?: { city?: string; state?: string };
  profileLink?: string;
  serviceTitle: string;
  status: EnquiryStatus;
  comments: EnquiryComment[];
  createdAt: string;
  updatedAt: string;
}

export interface MyLeadsParams {
  page?: number;
  limit?: number;
  serviceProductId?: string;
  businessId?: string;
  status?: EnquiryStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
  days?: number;
}

export interface MyLeadsResponse {
  success: boolean;
  message: string;
  data: {
    enquiries: Enquiry[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

export interface EnquiryResponse {
  success?: boolean;
  message?: string;
  data: Enquiry;
}

export const enquiryService = {
  async track(serviceProductId: string): Promise<EnquiryResponse> {
    return apiPost<EnquiryResponse>(API_ENDPOINTS.ENQUIRIES.TRACK, { _id: serviceProductId });
  },

  async myLeads(params: MyLeadsParams = {}): Promise<MyLeadsResponse> {
    const sp = new URLSearchParams();
    if (params.page) sp.append('page', params.page.toString());
    if (params.limit) sp.append('limit', params.limit.toString());
    if (params.serviceProductId) sp.append('serviceProductId', params.serviceProductId);
    if (params.businessId) sp.append('businessId', params.businessId);
    if (params.status) sp.append('status', params.status);
    if (params.search) sp.append('search', params.search);
    if (params.startDate) sp.append('startDate', params.startDate);
    if (params.endDate) sp.append('endDate', params.endDate);
    if (params.days !== undefined) sp.append('days', params.days.toString());
    const query = sp.toString();
    return apiGet<MyLeadsResponse>(
      `${API_ENDPOINTS.ENQUIRIES.MY_LEADS}${query ? `?${query}` : ''}`
    );
  },

  async getById(id: string): Promise<EnquiryResponse> {
    return apiGet<EnquiryResponse>(API_ENDPOINTS.ENQUIRIES.GET_BY_ID(id));
  },

  async updateStatus(id: string, status: EnquiryStatus): Promise<EnquiryResponse> {
    return apiPatch<EnquiryResponse>(API_ENDPOINTS.ENQUIRIES.UPDATE_STATUS(id), { status });
  },

  async addComment(id: string, text: string): Promise<EnquiryResponse> {
    return apiPost<EnquiryResponse>(API_ENDPOINTS.ENQUIRIES.ADD_COMMENT(id), { text });
  },

  async exportExcel(params: { businessId?: string; status?: EnquiryStatus; days?: number; startDate?: string; endDate?: string } = {}): Promise<{ blob: Blob; filename: string }> {
    const sp = new URLSearchParams();
    if (params.businessId) sp.append('businessId', params.businessId);
    if (params.status) sp.append('status', params.status);
    if (params.days !== undefined) sp.append('days', params.days.toString());
    if (params.startDate) sp.append('startDate', params.startDate);
    if (params.endDate) sp.append('endDate', params.endDate);
    const query = sp.toString();
    const url = `${API_ENDPOINTS.ENQUIRIES.EXPORT}${query ? `?${query}` : ''}`;
    const response = await (apiClient as any).client.get(url, { responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `leads-${new Date().toISOString().split('T')[0]}.xlsx`;
    return { blob: response.data as Blob, filename };
  },
};
